import {
  applyAction,
  type FactionId,
  type GameState,
  initGame,
  type TechCardId,
  type TriAngerAction,
  type UnitTypeId,
  type ValidationResult,
} from "@cloudnest/tri-anger-engine";
import { useSyncExternalStore } from "react";
import { isPlatformGamePath } from "../../shared/routing/game-path-mode";
import {
  createGameApi,
  type GameApi,
  type GameApiMode,
} from "../../shared/services/game-api";
import { getCurrentUserId } from "../../shared/store/auth-store";

let gameApi: GameApi | null = null;
let gameApiMode: GameApiMode | null = null;

function resolveTriAngerGameApiMode(): GameApiMode {
  if (typeof window === "undefined") return "legacy";
  return isPlatformGamePath("tri-anger", window.location.pathname)
    ? "game-platform"
    : "legacy";
}

function getGameApi(): GameApi {
  const nextMode = resolveTriAngerGameApiMode();
  if (gameApi === null || gameApiMode !== nextMode) {
    gameApi = createGameApi("tri-anger", nextMode);
    gameApiMode = nextMode;
  }
  return gameApi;
}

type ConnectionStatus =
  | "idle"
  | "connecting"
  | "open"
  | "reconnecting"
  | "closed";

export interface TriAngerMultiplayerState {
  readonly gameState: GameState | null;
  readonly history: readonly TriAngerAction[];
  readonly lastError: ValidationResult | null;
  readonly connection: ConnectionStatus;
  readonly lastAppliedSeq: number;
  readonly roomId: string | null;
  readonly myPlayerId: string | null;
  readonly isHost: boolean;
}

const INITIAL_STATE: TriAngerMultiplayerState = {
  gameState: null,
  history: [],
  lastError: null,
  connection: "idle",
  lastAppliedSeq: 0,
  roomId: null,
  myPlayerId: null,
  isHost: false,
};

let state: TriAngerMultiplayerState = { ...INITIAL_STATE };
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function parseFaction(value: unknown): FactionId | undefined {
  return value === "ECSU" || value === "GO" || value === "SB"
    ? value
    : undefined;
}

function parseFirstSeat(value: unknown): "host" | "guest" {
  return value === "guest" ? "guest" : "host";
}

function replayEventList(
  events: readonly {
    readonly eventType: string;
    readonly payload: Record<string, unknown>;
    readonly sequenceNumber: number;
  }[],
): {
  readonly gameState: GameState | null;
  readonly lastSeq: number;
  readonly history: readonly TriAngerAction[];
} {
  let gameState: GameState | null = null;
  let lastSeq = 0;
  const history: TriAngerAction[] = [];

  for (const event of events) {
    if (event.eventType === "init") {
      gameState = event.payload.state as unknown as GameState;
    } else if (gameState) {
      const action = eventPayloadToAction(event.eventType, event.payload);
      if (action) {
        const result = applyAction(gameState, action);
        if (result.ok) {
          gameState = result.state;
          history.push(action);
        }
      }
    }
    lastSeq = event.sequenceNumber;
  }

  return { gameState, lastSeq, history };
}

function eventPayloadToAction(
  eventType: string,
  payload: Record<string, unknown>,
): TriAngerAction | null {
  switch (eventType) {
    case "MoveUnit":
      return {
        type: "MoveUnit",
        playerId: payload.playerId as string,
        unitId: payload.unitId as string,
        to: payload.to as { x: number; y: number },
      };
    case "ProduceUnit":
      return {
        type: "ProduceUnit",
        playerId: payload.playerId as string,
        unitTypeId: payload.unitTypeId as UnitTypeId,
        workerUnitId: payload.workerUnitId as string | undefined,
      };
    case "BuildMulti":
      return {
        type: "BuildMulti",
        playerId: payload.playerId as string,
        at: payload.at as { x: number; y: number },
      };
    case "BuyTechCoin":
      return { type: "BuyTechCoin", playerId: payload.playerId as string };
    case "BuyTechCard":
      return {
        type: "BuyTechCard",
        playerId: payload.playerId as string,
        techCardId: payload.techCardId as TechCardId,
      };
    case "RevealTechCard":
      return {
        type: "RevealTechCard",
        playerId: payload.playerId as string,
        techCardId: payload.techCardId as TechCardId,
        target: payload.target as { x: number; y: number } | undefined,
      };
    case "EndPhase":
      return { type: "EndPhase", playerId: payload.playerId as string };
    case "Surrender":
      return { type: "Surrender", playerId: payload.playerId as string };
    default:
      return null;
  }
}

function actionToPayload(action: TriAngerAction): Record<string, unknown> {
  return action as unknown as Record<string, unknown>;
}

export const triAngerMultiplayerActions = {
  initializeAsHost: async (
    roomId: string,
    participantUserIds: readonly string[],
    playerNames: readonly string[] = [],
    config: Record<string, unknown> = {},
  ): Promise<void> => {
    const myPlayerId = getCurrentUserId() ?? "";
    state = {
      ...INITIAL_STATE,
      roomId,
      myPlayerId,
      isHost: true,
      connection: "connecting",
    };
    notify();

    const existingResponse = await getGameApi().getGameEvents(roomId);
    const existingEvents = existingResponse.success
      ? (existingResponse.data ?? [])
      : [];
    if (existingEvents.length > 0) {
      const replayed = replayEventList(existingEvents);
      state = {
        ...state,
        gameState: replayed.gameState,
        lastAppliedSeq: replayed.lastSeq,
        history: replayed.history,
        connection: "open",
      };
      notify();
      return;
    }

    const [hostId, guestId] = participantUserIds;
    if (!hostId || !guestId) {
      state = {
        ...state,
        lastError: { valid: false, error: "Tri-Anger requires two players." },
        connection: "closed",
      };
      notify();
      return;
    }

    const hostFaction = parseFaction(config.hostFaction) ?? "ECSU";
    const guestFallback = hostFaction === "GO" ? "SB" : "GO";
    const guestFaction = parseFaction(config.guestFaction) ?? guestFallback;
    const firstSeat = parseFirstSeat(config.firstSeat);
    const firstPlayerId = firstSeat === "guest" ? guestId : hostId;
    const gameState = initGame({
      gameId: roomId,
      players: [
        {
          id: hostId,
          name: playerNames[0] ?? "Player 1",
          faction: hostFaction,
        },
        {
          id: guestId,
          name: playerNames[1] ?? "Player 2",
          faction: guestFaction === hostFaction ? guestFallback : guestFaction,
        },
      ],
      firstPlayerId,
    });

    state = { ...state, gameState, connection: "open" };
    notify();

    try {
      const response = await getGameApi().submitEvent(roomId, "init", {
        state: gameState as unknown as Record<string, unknown>,
      });
      if (response.success && response.data) {
        state = { ...state, lastAppliedSeq: response.data.sequenceNumber };
        notify();
      }
    } catch (error) {
      state = {
        ...state,
        lastError: {
          valid: false,
          error:
            error instanceof Error
              ? error.message
              : "Tri-Anger initialization failed.",
        },
      };
      notify();
    }
  },

  initializeAsGuest: async (roomId: string): Promise<void> => {
    const myPlayerId = getCurrentUserId() ?? "";
    state = {
      ...INITIAL_STATE,
      roomId,
      myPlayerId,
      isHost: false,
      connection: "connecting",
    };
    notify();

    await triAngerMultiplayerActions.replayEvents(roomId);
    state = { ...state, connection: "open" };
    notify();
  },

  replayEvents: async (roomId: string): Promise<void> => {
    try {
      const response = await getGameApi().getGameEvents(roomId);
      if (!response.success || !response.data) return;
      const replayed = replayEventList(response.data);
      state = {
        ...state,
        gameState: replayed.gameState,
        lastAppliedSeq: replayed.lastSeq,
        history: replayed.history,
      };
      notify();
    } catch (error) {
      state = {
        ...state,
        lastError: {
          valid: false,
          error:
            error instanceof Error ? error.message : "Could not replay events.",
        },
      };
      notify();
    }
  },

  catchUpEvents: async (roomId: string): Promise<void> => {
    await triAngerMultiplayerActions.replayEvents(roomId);
  },

  applyRemoteEvent: (
    eventType: string,
    payload: Record<string, unknown>,
    seq: number,
  ): void => {
    if (seq <= state.lastAppliedSeq) return;
    if (eventType === "init") {
      state = {
        ...state,
        gameState: payload.state as unknown as GameState,
        lastAppliedSeq: seq,
      };
      notify();
      return;
    }
    if (!state.gameState) return;

    const action = eventPayloadToAction(eventType, payload);
    if (!action) {
      state = { ...state, lastAppliedSeq: seq };
      notify();
      return;
    }
    const result = applyAction(state.gameState, action);
    state = result.ok
      ? {
          ...state,
          gameState: result.state,
          history: [...state.history, action],
          lastAppliedSeq: seq,
        }
      : { ...state, lastAppliedSeq: seq };
    notify();
  },

  submitAction: async (
    action: TriAngerAction,
  ): Promise<{ readonly success: boolean; readonly error?: string }> => {
    if (!state.gameState || !state.roomId) {
      return { success: false, error: "No Tri-Anger game in progress." };
    }
    const result = applyAction(state.gameState, action);
    if (!result.ok) {
      state = { ...state, lastError: result.validation };
      notify();
      return { success: false, error: result.validation.error };
    }

    const roomId = state.roomId;
    const previousState = state.gameState;
    const previousHistory = state.history;
    state = {
      ...state,
      gameState: result.state,
      history: [...state.history, action],
      lastError: null,
    };
    notify();

    try {
      const response = await getGameApi().submitEvent(
        roomId,
        action.type,
        actionToPayload(action),
      );
      if (response.success && response.data) {
        state = { ...state, lastAppliedSeq: response.data.sequenceNumber };
        notify();
      }
      return { success: true };
    } catch (error) {
      state = {
        ...state,
        gameState: previousState,
        history: previousHistory,
        lastError: {
          valid: false,
          error:
            error instanceof Error ? error.message : "Action submit failed.",
        },
      };
      notify();
      return {
        success: false,
        error: error instanceof Error ? error.message : "Action submit failed.",
      };
    }
  },

  clearError: (): void => {
    state = { ...state, lastError: null };
    notify();
  },

  reset: (): void => {
    state = { ...INITIAL_STATE };
    notify();
  },
};

export const triAngerMultiplayerStore = {
  getSnapshot: (): TriAngerMultiplayerState => state,
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useTriAngerMultiplayer(): TriAngerMultiplayerState {
  return useSyncExternalStore(
    triAngerMultiplayerStore.subscribe,
    triAngerMultiplayerStore.getSnapshot,
  );
}
